
import { Shield, Eye, MonitorUp, Bell, Search, BarChart3 } from "lucide-react";
import { LandingPageTemplate } from "@/components/layout/LandingPageTemplate";
import { HeroSection } from "@/components/landing/HeroSection";
import { FeatureSection } from "@/components/landing/FeatureSection";
import { ProcessSection } from "@/components/landing/ProcessSection";
import { BenefitSection } from "@/components/landing/BenefitSection";
import { CtaSection } from "@/components/landing/CtaSection";

const ThreatMonitoring = () => {
  return (
    <LandingPageTemplate
      title="Threat Monitoring and Detection"
      description="Proactive threat monitoring and detection services to identify security incidents before they impact your business."
      keywords={[
        "threat monitoring", 
        "threat detection", 
        "security monitoring", 
        "SIEM", 
        "security operations", 
        "incident detection", 
        "security alerts"
      ]}
      canonicalUrl="/cybersecurity/threat-monitoring-and-detection/"
      schemaData={{
        name: "Threat Monitoring and Detection Service",
        description: "Proactive threat monitoring and detection services to identify security incidents before they impact your business.",
        provider: {
          name: "edgefi",
          sameAs: "https://edgefi.com"
        }
      }}
    >
      <HeroSection
        title="24/7"
        highlighted="Threat Monitoring & Detection"
        description="Identify potential security incidents in real-time with continuous monitoring of your environment by security experts who understand your business."
        ctaText="Get Protected Now"
        secondaryButtonText="View Solution Details"
        secondaryButtonLink="#features"
        modalTitle="Threat Monitoring Consultation"
        modalDescription="Tell us about your security monitoring needs and challenges, and we'll help you find the right solution."
        additionalFields={{
          lead_source: "Website",
          lead_interest: "Threat Monitoring",
          service_interest: "Security Operations"
        }}
        heroPattern="dots"
      />

      <FeatureSection 
        id="features"
        title="Comprehensive Threat Monitoring"
        subtitle="SECURITY OPERATIONS"
        description="Our threat monitoring and detection service provides continuous visibility into your security posture."
        background="light"
        features={[
          {
            title: "24/7 Monitoring",
            description: "Round-the-clock monitoring of your network, systems, and applications for suspicious activities and potential threats.",
            icon: <Eye className="h-6 w-6 text-purple-600" />
          },
          {
            title: "Advanced SIEM Platform",
            description: "Enterprise-grade security information and event management (SIEM) technology to collect, analyze, and correlate security data.",
            icon: <MonitorUp className="h-6 w-6 text-purple-600" />
          },
          {
            title: "Real-time Alerting",
            description: "Immediate notification of security incidents through customized alerting workflows based on severity and impact.",
            icon: <Bell className="h-6 w-6 text-purple-600" />
          },
          {
            title: "Threat Intelligence",
            description: "Integration of current threat intelligence to detect emerging threats and attack patterns targeting your industry.",
            icon: <Search className="h-6 w-6 text-purple-600" />
          },
          {
            title: "Security Analytics",
            description: "Advanced analytics and machine learning to identify abnormal behavior patterns and potential security incidents.",
            icon: <BarChart3 className="h-6 w-6 text-purple-600" />
          },
          {
            title: "Expert Triage",
            description: "Security analysts review and validate alerts to eliminate false positives and provide actionable information.",
            icon: <Shield className="h-6 w-6 text-purple-600" />
          }
        ]}
      />

      <ProcessSection 
        title="How Our Monitoring Solution Works"
        subtitle="PROCESS"
        description="A systematic approach to detecting and responding to security threats in your environment."
        steps={[
          {
            title: "Log Collection",
            description: "Gather security logs and events from across your IT infrastructure and cloud environments."
          },
          {
            title: "Normalization & Analysis",
            description: "Process and analyze data to identify patterns, anomalies, and potential security incidents."
          },
          {
            title: "Alert Triage",
            description: "Security analysts validate alerts to eliminate false positives and determine severity."
          },
          {
            title: "Notification",
            description: "Communicate validated security incidents to your team through defined escalation procedures."
          },
          {
            title: "Response Guidance",
            description: "Provide recommendations and support for containing and remediating security incidents."
          }
        ]}
        layout="horizontal"
      />

      <BenefitSection 
        title="Business Benefits of Threat Monitoring"
        subtitle="VALUE PROPOSITION"
        background="gradient"
        benefits={[
          {
            title: "Reduced Detection Time",
            description: "Identify security incidents in minutes or hours instead of days or weeks."
          },
          {
            title: "Lower Breach Impact",
            description: "Minimize the financial and reputational damage from security incidents through early detection."
          },
          {
            title: "24/7 Coverage",
            description: "Maintain continuous security monitoring without the cost of staffing an internal SOC."
          },
          {
            title: "Compliance Support",
            description: "Meet regulatory requirements for security monitoring and incident detection."
          },
          {
            title: "Improved Visibility",
            description: "Gain comprehensive visibility into your security posture across all environments."
          },
          {
            title: "Threat Intelligence",
            description: "Benefit from security expertise and threat intelligence that would be costly to maintain internally."
          }
        ]}
      />

      <CtaSection 
        title="Protect Your Business with 24/7 Threat Monitoring"
        description="Get started with a security assessment to understand your monitoring needs and build a customized solution."
        ctaText="Schedule Your Assessment"
        modalTitle="Threat Monitoring Assessment"
        modalDescription="Let's discuss your security monitoring requirements and design a solution that fits your business needs."
        additionalFields={{
          lead_source: "Website",
          lead_interest: "Threat Monitoring",
          service_interest: "Security Assessment"
        }}
      />
    </LandingPageTemplate>
  );
};

export default ThreatMonitoring;
